// Every backend call of the shared enquiry screens, so the paths live in one place.
// Contract: mbp_crm-adminserver-shared db/phase3/shared-enquiry-API.md.
import axios from '../../axios'

const PATHS = {
  // not v1/postalAreas: that is the settings list
  postcodeAreas: '/v1/postcodeAreas',
  projects: '/v1/projects',
  phases: projectId => '/v1/projects/' + projectId + '/phases',
  sharedEnquiries: '/v1/sharedEnquiries',
  sharedEnquiry: id => '/v1/sharedEnquiries/' + id,
  checkCustomer: id => '/v1/sharedEnquiries/' + id + '/checkCustomer',
  bids: id => '/v1/sharedEnquiries/' + id + '/bids',
  attachments: id => '/v1/sharedEnquiries/' + id + '/attachments',
  fieldSettings: '/v1/enquiryFieldSettings',
  valueBasis: '/v1/enquiryValueBasis',
  // one page (design B) or tabs (design D), be4's setting (Mohammed 20/10)
  formLayout: '/v1/enquiryFormLayout',
  // FE4 Duplicate Projects (BE4 contract: db/phase3/BE4-contract-duplicate-projects.md)
  duplicateCounts: '/v1/duplicateProjects/counts',
  duplicatePairs: '/v1/duplicateProjects',
  dismissPair: pairId => '/v1/duplicateProjects/' + pairId + '/dismiss',
  mergePreview: keepId => '/v1/projects/' + keepId + '/mergePreview',
  merge: keepId => '/v1/projects/' + keepId + '/merge',
  // existing CRM endpoints, unchanged
  searchCustomers: text => '/v1/searchedcustomer/' + encodeURIComponent(text),
  contacts: customerId => '/v1/activeCustomerContacts/' + customerId,
  branches: customerId => '/v1/getEnquiryCustomerMBPbranches/' + customerId,
  productTypes: '/v1/activeProductTypes',
  projectTypes: '/v1/activeProjectTypes',
  users: '/v1/activeUsers'
}

export { PATHS }

async function call(method, path, body, params) {
  const response = method === 'get' || method === 'delete'
    ? await axios[method](path, { params })
    : await axios[method](path, body)
  return response.data
}

export default {
  postcodeAreas: prefix => call('get', PATHS.postcodeAreas, null, { prefix }),
  projects: (q, postcodeArea) => call('get', PATHS.projects, null, { q: q || '', postcodeArea: postcodeArea || '' }),
  createProject: body => call('post', PATHS.projects, body),
  addPhase: (projectId, body) => call('post', PATHS.phases(projectId), body),
  createSharedEnquiry: body => call('post', PATHS.sharedEnquiries, body),
  updateSharedEnquiry: (id, body) => call('put', PATHS.sharedEnquiry(id), body),
  sharedEnquiry: id => call('get', PATHS.sharedEnquiry(id)),
  // branchId: same customer + product type + branch is BLOCK, another branch WARN (decision 18/10)
  checkCustomer: (id, customerId, productTypeId, branchId) =>
    call('post', PATHS.checkCustomer(id), { customerId, productTypeId, branchId: branchId || null }),
  addBid: (id, body) => call('post', PATHS.bids(id), body),
  attachments: id => call('get', PATHS.attachments(id)),
  uploadAttachment: (id, file, category) => {
    const form = new FormData()
    form.append('file', file)
    form.append('category', category)
    return call('post', PATHS.attachments(id), form)
  },
  fieldSettings: () => call('get', PATHS.fieldSettings),
  // the two PUTs are Admin only (role showAdmin): anyone else gets a 403 refusal
  saveFieldSettings: rows => call('put', PATHS.fieldSettings, rows),
  valueBasis: () => call('get', PATHS.valueBasis),
  saveValueBasis: basis => call('put', PATHS.valueBasis, { basis }),
  // {layout: 'ONE_PAGE' | 'TABS'}; the PUT is Admin only, as the other settings
  formLayout: () => call('get', PATHS.formLayout),
  saveFormLayout: layout => call('put', PATHS.formLayout, { layout }),
  duplicateCounts: () => call('get', PATHS.duplicateCounts),
  // a bare array, with the pre-paging match count in the RecCount header (axios lower-cases it to reccount)
  duplicatePairs: params => axios.get(PATHS.duplicatePairs, { params }).then(response => {
    const rows = response.data || []
    const header = response.headers ? parseInt(response.headers.reccount) : NaN
    return { rows, recCount: isNaN(header) ? rows.length : header }
  }),
  dismissPair: pairId => call('post', PATHS.dismissPair(pairId), {}),
  mergePreview: (keepId, otherId) => call('get', PATHS.mergePreview(keepId), null, { other: otherId }),
  mergeProjects: (keepId, otherId) => call('post', PATHS.merge(keepId), { otherProjectId: otherId }),
  // the customer search sends custCode and address4 (the town); the pickers show accountCode and town
  searchCustomers: text => call('get', PATHS.searchCustomers(text))
    .then(rows => (rows || []).map(c => ({ ...c, accountCode: c.custCode, town: c.address4 }))),
  contacts: customerId => call('get', PATHS.contacts(customerId)),
  branches: customerId => call('get', PATHS.branches(customerId)),
  productTypes: () => call('get', PATHS.productTypes),
  projectTypes: () => call('get', PATHS.projectTypes),
  users: () => call('get', PATHS.users)
}
